"use client";

/**
 * Render plain user text with its URLs turned into links.
 *
 * This is phase zero of link previews and useful on its own: Hub renders note
 * and comment bodies as raw text today, so a pasted URL is dead characters
 * somebody has to select and copy. Nothing here fetches anything.
 *
 * Three deliberate choices:
 *
 *  - **`rel="noopener noreferrer"`.** `noopener` is the familiar one. `noreferrer`
 *    is the one that matters more here: without it the destination learns the
 *    Hub URL the link was clicked from, and our URLs carry entity ids. A note
 *    about a deal should not tell a vendor which deal it was.
 *  - **Text, never markup.** The input is a plain string rendered as React
 *    children, so it is escaped. There is no `dangerouslySetInnerHTML` path in
 *    or out of this component, by construction.
 *  - **Display text may be shortened; the href never is.** A 400-character URL
 *    otherwise wrecks the layout of every note that mentions it. The host stays
 *    visible in the shortened form — truncating the front would let
 *    `evil.com/?x=hub.westtn.consulting/...` read as one of ours.
 */

import type { ReactElement, ReactNode } from "react";

import { splitOnUrls } from "../extract-urls";

/** Long enough for a real URL, short enough not to break a card. */
const DEFAULT_MAX_DISPLAY_CHARS = 72;

export interface LinkifiedTextProps {
  text: string;
  /** Applied to the wrapper. Callers rendering notes want `whitespace-pre-wrap`. */
  className?: string;
  linkClassName?: string;
  /** 0 disables shortening. */
  maxDisplayChars?: number;
  /**
   * Rendered after each link — the seam the preview card plugs into in a later
   * phase, so this component never has to know how previews are fetched.
   */
  renderAfterLink?: (url: string) => ReactNode;
}

/**
 * Shorten for display, keeping the scheme and host intact and eliding the
 * middle of the path. `https://example.com/a/very/long/path/to/a/file.pdf`
 * becomes `https://example.com/a/very...file.pdf`.
 */
export function shortenForDisplay(url: string, maxChars: number): string {
  if (maxChars <= 0 || url.length <= maxChars) return url;

  let origin = "";
  try {
    origin = new URL(url).origin;
  } catch {
    origin = url.slice(0, Math.floor(maxChars / 2));
  }

  const rest = url.slice(origin.length);
  const budget = maxChars - origin.length - 3;
  if (budget <= 4) return `${origin}...`;

  const head = Math.ceil(budget / 2);
  const tail = budget - head;
  return `${origin}${rest.slice(0, head)}...${rest.slice(rest.length - tail)}`;
}

export function LinkifiedText({
  text,
  className,
  linkClassName,
  maxDisplayChars = DEFAULT_MAX_DISPLAY_CHARS,
  renderAfterLink,
}: LinkifiedTextProps): ReactElement {
  const segments = splitOnUrls(text);

  return (
    <span className={className}>
      {segments.map((segment, index) =>
        segment.kind === "url" ? (
          // Index is a stable key here: segments are derived from `text` in
          // order, so a given index always holds the same piece of that text.
          <span key={`u${index}`}>
            <a
              href={segment.value}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClassName}
              title={segment.value}
            >
              {shortenForDisplay(segment.value, maxDisplayChars)}
            </a>
            {renderAfterLink?.(segment.value)}
          </span>
        ) : (
          <span key={`t${index}`}>{segment.value}</span>
        )
      )}
    </span>
  );
}
